import {
  ApiChartAttribute,
  ApiChartLayout,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiOperator,
  ApiYAxisChartType,
} from "@incendium/api";
import { Box, Stack } from "@mui/material";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_DASHBOARD_CONTENT_IMPROVE_BAR,
  CHART_KEY_DASHBOARD_CONTENT_TABLE,
  CHART_KEY_DASHBOARD_CONTENT_TOP_BAR,
} from "features/analytics/constants/chartKeys";
import { table5Rows } from "features/analytics/constants/constants";
import { useMemo } from "react";
import {
  dashboardChartKey,
  revenueOrConversions,
  useIsEcommerceProject,
  withoutAddToCartMetrics,
} from "features/dashboard/utils/ecommerceMetrics";

interface IContentPerformanceTabProps {
  attributes: ApiChartAttribute[];
}

function ContentPerformanceTab({ attributes }: IContentPerformanceTabProps) {
  const isEcommerce = useIsEcommerceProject();

  const tableFields = useMemo(
    () =>
      withoutAddToCartMetrics(
        [
          ApiMetric.METRIC_PAGEVIEWS_COUNT,
          ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
          ApiMetric.METRIC_PAGE_MACRO_CONVERSION_RATE,
          ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
          ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
          revenueOrConversions(isEcommerce),
        ],
        isEcommerce,
      ),
    [isEcommerce],
  );

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ flexShrink: 0, width: "100%", minWidth: 0 }}
      >
        <Box sx={{ height: { xs: 320, md: 400 }, flex: 1, minWidth: 0 }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_CONTENT_TOP_BAR}
            staticAttributes={attributes}
            ignoreAttrModel
            chart={{
              name: "Top Content",
              dimension: [ApiDimension.DIMENSION_LOCATION],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes: [],
              limit: 10,
              orderBy: `${ApiMetric.METRIC_PAGEVIEWS_COUNT} DESC`,
              having: [
                {
                  metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  operator: ApiOperator.IS_GREATER_THAN,
                  value: 0,
                },
              ],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_PAGEVIEWS_COUNT],
                  chart: {
                    [ApiMetric.METRIC_PAGEVIEWS_COUNT]: ApiYAxisChartType.BAR,
                  },
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                layout: ApiChartLayout.VERTICAL,
                noLegend: true,
              },
            }}
          />
        </Box>

        <Box sx={{ height: { xs: 320, md: 400 }, flex: 1, minWidth: 0 }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_CONTENT_IMPROVE_BAR}
            staticAttributes={attributes}
            ignoreAttrModel
            chart={{
              name: "Content to Improve",
              dimension: [ApiDimension.DIMENSION_LOCATION],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes: [],
              limit: 10,
              orderBy: `${ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE} ASC`,
              having: [
                {
                  metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  operator: ApiOperator.IS_GREATER_THAN,
                  value: 0,
                },
              ],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE],
                  chart: {
                    [ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE]:
                      ApiYAxisChartType.BAR,
                  },
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                layout: ApiChartLayout.VERTICAL,
                noLegend: true,
              },
            }}
          />
        </Box>
      </Stack>

      <Box
        sx={{
          height: table5Rows,
          flexShrink: 0,
          width: "100%",
          minWidth: 0,
          overflowX: "auto",
        }}
      >
        <StandardAnalyticsCard
          chartKey={dashboardChartKey(
            CHART_KEY_DASHBOARD_CONTENT_TABLE,
            isEcommerce,
          )}
          staticAttributes={attributes}
          ignoreAttrModel
          chart={{
            name: "Pageview Performance",
            dimension: [ApiDimension.DIMENSION_LOCATION],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            orderBy: `${ApiMetric.METRIC_PAGEVIEWS_COUNT} DESC`,
            having: [
              {
                metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                operator: ApiOperator.IS_GREATER_THAN,
                value: 0,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: tableFields,
              },
            ],
            displayOptions: {
              noFilters: true,
              noAttributionToggle: true,
              rowsPerPage: 0,
              showTotals: true,
            },
          }}
        />
      </Box>
    </>
  );
}

export default ContentPerformanceTab;
